import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ArrowRight } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import { Button } from "../components/ui/button";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import ContentSections from "../components/public/ContentSections";
import RelatedContent from "../components/public/RelatedContent";
import ProductCard from "../components/common/ProductCard";
import api from "../lib/api";

export default function ContentPage({ slug, fallbackTitle, showAreasCta = false }) {
  const [page, setPage] = useState(null);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setRelated(null);
    api.get(`/content/pages/${slug}`)
      .then(({ data }) => {
        setPage(data);
        api.get("/related-content", { params: { entity_type: "page", slug } })
          .then(({ data: rc }) => setRelated(rc)).catch(() => {});
      })
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  if (!page) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24" data-testid="content-page-empty">
        <SectionTitle eyebrow="Information" title={fallbackTitle || "Coming soon"} />
        <GlassCard className="mt-8 p-10 text-center text-muted-foreground" hover={false}>
          This page is being prepared and will be published shortly.
        </GlassCard>
      </div>
    );
  }

  const featured = related?.products || [];

  return (
    <div className="py-20" data-testid="content-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <Breadcrumbs className="mb-6" />
        <h1 className="font-playfair text-3xl sm:text-4xl text-white">{page.title}</h1>
        <div className="mt-8 prose prose-invert max-w-none text-white/80" data-testid="content-page-body" dangerouslySetInnerHTML={{ __html: page.content_html }} />
        <div className="mt-10"><ContentSections content={page.content_seo} flat /></div>
        {showAreasCta && (
          <GlassCard className="mt-10 p-6 sm:p-8" hover={false} data-testid="about-areas-cta">
            <div className="flex items-center gap-2 text-gold"><MapPin className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">Local delivery area</span></div>
            <p className="mt-3 text-white/80 leading-relaxed">
              Clacton Fireworks serves customers across Weeley, Clacton-on-Sea, Tendring and surrounding villages
              throughout North Essex. Customers can reserve online and collect from Weeley Post Office &amp; Store.
            </p>
            <Link to="/areas-we-serve" className="mt-5 inline-block" data-testid="about-areas-link">
              <Button className="cf-gold-btn font-semibold gap-2">View Areas We Serve <ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </GlassCard>
        )}
      </div>

      {(featured.length > 0 || related?.categories?.length || related?.brands?.length ||
        Object.keys(related?.links_by_location || {}).length) ? (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {featured.length > 0 && (
            <div className="mt-14" data-testid="landing-featured-products">
              <h2 className="font-playfair text-2xl text-white mb-5">Featured Fireworks</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featured.map((r, i) => <ProductCard key={r.product_id} product={r} index={i} />)}
              </div>
            </div>
          )}
          <RelatedContent data={related} />
        </div>
      ) : null}
    </div>
  );
}
